import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Handshake, Plus, Trash2, KeyRound, Loader2 } from "lucide-react";

const BLANK = { username: "", password: "", name: "" };

export default function SuperSalesUsers() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const load = () => api.get("/platform/sales-users").then(({ data }) => setItems(data)).catch((e) => toast.error(extractErr(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/platform/sales-users", form);
      toast.success("Sales user created");
      setForm(BLANK);
      load();
    } catch (err) { toast.error(extractErr(err)); }
    finally { setSaving(false); }
  };

  const savePassword = async () => {
    if (!pwTarget) return;
    setPwSaving(true);
    try {
      await api.put(`/platform/sales-users/${pwTarget.id}`, { password: newPassword });
      toast.success(`Password updated for ${pwTarget.username}`);
      setPwTarget(null);
      setNewPassword("");
    } catch (err) { toast.error(extractErr(err)); }
    finally { setPwSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete sales user "${u.username}"? They will no longer be able to sign in.`)) return;
    try {
      await api.delete(`/platform/sales-users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(extractErr(err)); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-100 flex items-center gap-2">
          <Handshake className="w-7 h-7 text-teal-300" /> Sales users
        </h1>
        <p className="text-slate-400 text-sm mt-1">Create logins for the Sales panel. Sales users can manage clients and delete unprotected stores. Super admin can lock a store so sales cannot delete it.</p>
      </div>

      <Card className="bg-slate-900 border-slate-800 p-5 text-slate-100">
        <form onSubmit={create} className="space-y-4" data-testid="sales-user-create-form">
          <div className="flex items-center gap-2 text-teal-300"><Plus className="w-4 h-4" /><span className="text-sm font-semibold">New sales user</span></div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-300">Username</Label>
              <Input required minLength={3} data-testid="sales-user-username" className="mt-1 bg-slate-950 border-slate-700" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="sales.ravi" />
            </div>
            <div>
              <Label className="text-slate-300">Display name</Label>
              <Input data-testid="sales-user-name" className="mt-1 bg-slate-950 border-slate-700" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ravi" />
            </div>
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input required minLength={6} type="password" data-testid="sales-user-password" className="mt-1 bg-slate-950 border-slate-700" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="sales-user-create" className="bg-teal-600 hover:bg-teal-500 text-white">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create sales user"}
          </Button>
        </form>
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-0 overflow-hidden text-slate-100">
        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No sales users yet.</div>
        ) : (
          <div className="divide-y divide-slate-800">
            {items.map((u) => (
              <div key={u.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`sales-user-row-${u.username}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-100">{u.name}</div>
                  <div className="text-xs font-mono-nums text-slate-400">{u.username}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100" onClick={() => { setPwTarget(u); setNewPassword(""); }} data-testid={`sales-user-pw-${u.username}`}>
                    <KeyRound className="w-4 h-4 mr-1.5" /> Change password
                  </Button>
                  <Button variant="outline" size="sm" className="border-red-800 bg-red-950/40 hover:bg-red-900/60 text-red-300" onClick={() => remove(u)} data-testid={`sales-user-delete-${u.username}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {pwTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="sales-user-pw-modal">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl p-6 space-y-4">
            <h2 className="font-display text-lg font-bold text-slate-100">Change password</h2>
            <p className="text-sm text-slate-400">Set a new password for <b className="text-white">{pwTarget.username}</b>. They will need to sign in again.</p>
            <div>
              <Label className="text-slate-300">New password</Label>
              <Input type="password" minLength={6} autoFocus data-testid="sales-user-pw-input" className="mt-1 bg-slate-800 border-slate-700 text-slate-100" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPwTarget(null)} disabled={pwSaving} className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100">Cancel</Button>
              <Button onClick={savePassword} disabled={pwSaving || newPassword.length < 6} data-testid="sales-user-pw-save" className="bg-teal-600 hover:bg-teal-500 text-white">
                {pwSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save password"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
